import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuth } from '../hooks/use-auth';

interface ProtectedRouteProps {
    children: React.ReactNode;
}

/**
 * ProtectedRoute component that guards routes requiring authentication
 * Redirects to login page if user is not authenticated
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const { user, isLoading, checkAuth } = useAuth();
    const location = useLocation();
    const [hasChecked, setHasChecked] = useState(false);

    useEffect(() => {
        // Only check authentication once on mount
        if (!hasChecked) {
            checkAuth().finally(() => setHasChecked(true));
        }
    }, [hasChecked, checkAuth]);

    // Show loading spinner while checking authentication or haven't checked yet
    if (isLoading || !hasChecked) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Spin size="large" tip="Checking authentication..." />
            </div>
        );
    }

    // Redirect to login if not authenticated, preserving the intended destination
    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // User is authenticated, render the protected content
    return <>{children}</>;
};
